import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firestore';
import { useAuthContext } from '../context/AuthProvider';
import { AppRole, roleLabel } from '../rbac';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Require the user's role to be at least this rank. */
  minRole?: AppRole;
  /** Require the user to hold this permission token (or the 'all' wildcard). */
  requirePerm?: string;
}

/**
 * Route guard.
 *
 * - No `minRole`/`requirePerm`  -> legacy behaviour: authenticated users pass
 *   (keeps every existing admin route working, incl. pre-RBAC-migration admins
 *   whose tokens may not carry claims yet).
 * - With a gating prop           -> enforces RBAC claims: active account + role
 *   rank / permission. Denials render an in-place 403 (no redirect loop).
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, minRole, requirePerm }) => {
  const { user, loading, claims, active, hasRole, hasPerm } = useAuthContext();
  const navigate = useNavigate();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!loading) return;
    const timeout = setTimeout(() => setTimedOut(true), 10000);
    return () => clearTimeout(timeout);
  }, [loading]);

  if (loading && !timedOut) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    if (loading && timedOut) {
      return (
        <div className="loading-container">
          <p>Unable to verify login status. This can happen due to a slow connection or browser settings.</p>
          <p style={{ marginTop: '10px', fontSize: '0.9rem', opacity: 0.8 }}>
            Try: clearing your browser cache, disabling extensions, or using a different browser.
          </p>
          <button
            onClick={() => (window.location.href = '/admin/login')}
            style={{
              marginTop: '15px',
              padding: '10px 24px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            Back to Login
          </button>
        </div>
      );
    }
    return <Navigate to="/admin/login" />;
  }

  const gated = Boolean(minRole || requirePerm);

  // Legacy routes (no gate): presence of a user is sufficient — unchanged.
  if (!gated) {
    return <>{children}</>;
  }

  // --- RBAC-gated route below: FAIL-CLOSED. ---
  // Ungated (legacy) routes already returned above, so reaching here means the
  // route requires a role/permission. A claimless session is denied (handled
  // right after `denied` is defined). This closes the previous fail-OPEN behavior
  // where any signed-in-but-claimless session (incl. a self-registered account)
  // passed every gated route at the UI-shell level. Server rules already deny such
  // sessions all data; this aligns the client. Every provisioned admin/employee
  // carries claims, and /admin/dashboard stays ungated, so no login is blocked.

  const denied = (title: string, message: string, allowSignOut = false) => (
    <div
      className="loading-container"
      style={{ flexDirection: 'column', textAlign: 'center', padding: '2rem', color: '#fff' }}
    >
      <h2 style={{ color: '#ff6b6b', marginBottom: '0.5rem' }}>{title}</h2>
      <p style={{ opacity: 0.85, maxWidth: 420 }}>{message}</p>
      <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={() => navigate('/admin')}
          style={{
            padding: '10px 24px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem',
          }}
        >
          Go to my dashboard
        </button>
        {allowSignOut && (
          <button
            onClick={() => {
              signOut(auth).finally(() => (window.location.href = '/admin/login'));
            }}
            style={{
              padding: '10px 24px',
              background: 'transparent',
              color: '#3b82f6',
              border: '1px solid #3b82f6',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            Sign out
          </button>
        )}
      </div>
    </div>
  );

  // Claimless session on a gated route → deny (fail-closed). A real admin/employee
  // whose token is briefly stale can refresh by signing out and back in.
  if (!claims) {
    return denied(
      'Access not provisioned',
      "Your account isn't provisioned for this area. If you were just added or your role changed, sign out and back in to refresh your access, or contact an administrator.",
      true
    );
  }

  // Suspended / terminated — mirror of the server `active == true` gate.
  if (claims && !active) {
    return denied(
      'Account inactive',
      'Your account is inactive. Contact an administrator to be reactivated.',
      true
    );
  }

  if (minRole && !hasRole(minRole)) {
    return denied(
      'Not authorized',
      `This area requires ${roleLabel(minRole)} access or higher.`
    );
  }

  if (requirePerm && !hasPerm(requirePerm)) {
    return denied('Not authorized', "You don't have permission to view this area.");
  }

  return <>{children}</>;
};

export default ProtectedRoute;
